import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { ThreeDots } from 'react-loader-spinner';
import SftGlobalData from '../library/globalData';
import './AuthScreen.css';

function AuthScreen({ onLogin, singleDocMode = false }) {
    const { key, sftKey, redirect, lang } = useParams();
    const navigate = useNavigate();
    const [error, setError] = useState(null);

    const targetLang = lang || 'nl';

    useEffect(() => {
        handleAuth();
    }, []);

    const handleAuth = async () => {
        try {
            // Handle mysft route - client-side decryption
            if (sftKey) {
                try {
                    // Replace DIRTY_SLASH_FIX with actual slashes
                    const fixedKey = sftKey.replaceAll('DIRTY_SLASH_FIX', '/');

                    // Decrypt the key
                    const decrypted = CryptoJS.AES.decrypt(fixedKey, SftGlobalData.encryptionKey);
                    const decryptedData = JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));

                    if (SftGlobalData.debug) {
                        console.log('Decrypted mysft data:', decryptedData);
                    }

                    // Check if user has valid group
                    if (decryptedData.sft_group === 'ADMIN' ||
                        decryptedData.sft_group === 'SUPER' ||
                        decryptedData.sft_group === 'SUPP' ||
                        decryptedData.sft_group === 'DEV') {

                        onLogin({
                            sft_user_id: decryptedData.sft_user_id,
                            sft_status: decryptedData.sft_status,
                            sft_group: decryptedData.sft_group
                        });

                        navigate(`/${targetLang}/`);
                        return;
                    } else {
                        setError('Unauthorized access');
                    }
                } catch (decryptError) {
                    console.error('Decryption error:', decryptError);
                    setError('Invalid authentication token');
                }
                return;
            }

            // Handle auth route - API call
            if (key) {
                const response = await SftGlobalData.post('auth', { key });

                if (response.success && response.data) {
                    if (response.data.dbs && response.data.dbs.length > 0) {
                        onLogin({
                            sft_user_id: response.data.sft_user_id,
                            sft_status: response.data.sft_status,
                            sft_group: response.data.sft_group
                        });

                        navigate(`/${targetLang}/`);
                        return;
                    }
                }
                setError(response?.message || 'Authentication failed');
                return;
            }

            // Handle redirect route - API call to fetch encrypted doc
            if (redirect) {
                const response = await SftGlobalData.post('docs&action=fetchEncrypted', { crypt: redirect });

                console.log('fetchEncrypted response:', response);

                // Check for document ID in response
                const docId = response.data?.id || response.id;

                console.log('docId:', docId, 'singleDocMode:', singleDocMode);

                if (response.success && docId) {
                    onLogin({
                        sft_user_id: response.data?.sft_user_id || response.sft_user_id,
                        sft_status: response.data?.sft_status || response.sft_status,
                        sft_group: response.data?.sft_group || response.sft_group
                    });

                    // Use /view/ for single doc mode (no sidebar), /document/ for full mode
                    const path = singleDocMode ? 'view' : 'document';
                    const targetUrl = `/${targetLang}/${path}/${docId}`;
                    console.log('Navigating to:', targetUrl);
                    navigate(targetUrl);
                    return;
                }
                setError(response?.message || 'Invalid redirect token');
                return;
            }

            // No valid auth method
            setError('No authentication method provided');

        } catch (err) {
            setError('An error occurred during authentication');
            console.error('Auth error:', err);
        }
    };

    if (error) {
        return (
            <div className="auth-screen">
                <div className="auth-error fade-scale-in">
                    <h2>Authentication Error</h2>
                    <p>{error}</p>
                    <button onClick={() => navigate(`/${targetLang}/`)}>
                        Go to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-screen">
            <div className="auth-loading fade-scale-in">
                <ThreeDots color="#0071BB" height={60} width={60} />
                <p>Authenticating...</p>
            </div>
        </div>
    );
}

export default AuthScreen;
